import { collection, addDoc, query, where, orderBy, limit, getDocs, Timestamp, deleteDoc } from 'firebase/firestore';
import * as Sentry from '@sentry/react';

const ACTIVITY_COLLECTION = 'admin_activities';
const RETENTION_DAYS = 7;

export const ACTIVITY_TYPES = {
    CREATE: 'create',
    UPDATE: 'update',
    DELETE: 'delete',
    VIEW: 'view',
    ACTIVATE: 'activate',
    DEACTIVATE: 'deactivate',
    PERMISSION_CHANGE: 'permission_change',
    UPLOAD: 'upload',
    DOWNLOAD: 'download',
    EXPORT: 'export',
    IMPORT: 'import'
};

export const ACTIVITY_MODULES = {
    BOOKS: 'books',
    BOOK_SECTIONS: 'book_sections',
    BOOK_TOOLS: 'book_tools',
    USERS: 'users',
    PERMISSIONS: 'permissions',
    SETTINGS: 'settings',
    PLACEMENT_EXAM: 'placement_exam',
    GRAMMAR_DRILL: 'grammar_drill',
    QUOTES: 'quotes',
    STANDARDS: 'standards',
    INAPPROPRIATE_WORDS: 'inappropriate_words',
    CLASSES: 'classes',
    ORGANIZATIONS: 'organizations',
    ASSIGNMENTS: 'assignments',
    ACTIVITIES: 'activities',
    QUIZZES: 'quizzes',
    RESOURCES: 'resources',
    VIDEOS: 'videos',
    STORY: 'story',
    VOCAB: 'vocabulary',
    SLIDES: 'slides',
    RUBRICS: 'rubrics',
    LESSON_PLANS: 'lesson_plans',
    WORKSHEETS: 'worksheets'
};

export class AdminActivityLogger {
    constructor(firestore, currentUser, firebaseUser) {
        this.firestore = firestore;
        this.currentUser = currentUser;
        this.firebaseUser = firebaseUser;
    }

    isAdminUser() {
        return this.currentUser?.role === 'owe_admin';
    }

    async logActivity({
        activityType,
        module,
        action,
        resourceId = null,
        resourceName = null,
        details = {},
        metadata = {}
    }) {
        if (!this.isAdminUser() || !this.firestore || !this.firebaseUser) {
            return null;
        }

        try {
            const activityData = {
                userId: this.currentUser.id,
                userName: `${this.currentUser.firstName || ''} ${this.currentUser.lastName || ''}`.trim(),
                userEmail: this.currentUser.email || '',
                activityType,
                module,
                action,
                resourceId,
                resourceName,
                details,
                metadata: {
                    ...metadata,
                    userAgent: navigator.userAgent,
                    platform: navigator.platform,
                    screenResolution: `${window.screen.width}x${window.screen.height}`
                },
                timestamp: Timestamp.now(),
                createdAt: new Date().toISOString()
            };

            const docRef = await addDoc(
                collection(this.firestore, ACTIVITY_COLLECTION),
                activityData
            );

            return docRef.id;
        } catch (error) {
            console.error('Failed to log admin activity:', error);
            Sentry.captureException(error, {
                tags: { module: 'AdminActivityLogger' },
                extra: { activityType, module, action }
            });
            return null;
        }
    }

    async logBookActivity(activityType, bookId, bookName, details = {}) {
        // Use the action from details if provided, otherwise use the generic action
        const action = details.action || `${activityType}_book`;
        // Remove action from details to avoid duplication
        const { action: _, ...detailsWithoutAction } = details;
        
        return this.logActivity({
            activityType,
            module: ACTIVITY_MODULES.BOOKS,
            action: action,
            resourceId: bookId,
            resourceName: bookName,
            details: detailsWithoutAction
        });
    }

    async logBookToolActivity(activityType, toolName, bookId, pageId, details = {}) {
        return this.logActivity({
            activityType,
            module: ACTIVITY_MODULES.BOOK_TOOLS,
            action: `${activityType}_${toolName}`,
            resourceId: pageId || bookId,
            resourceName: toolName,
            details: {
                ...details,
                bookId,
                pageId,
                toolName
            }
        });
    }

    async logUserActivity(activityType, targetUserId, targetUserName, details = {}) {
        return this.logActivity({
            activityType,
            module: ACTIVITY_MODULES.USERS,
            action: `${activityType}_user`,
            resourceId: targetUserId,
            resourceName: targetUserName,
            details
        });
    }

    async logPermissionActivity(targetUserId, targetUserName, permissionChanges) {
        return this.logActivity({
            activityType: ACTIVITY_TYPES.PERMISSION_CHANGE,
            module: ACTIVITY_MODULES.PERMISSIONS,
            action: 'update_permissions',
            resourceId: targetUserId,
            resourceName: targetUserName,
            details: {
                permissionChanges
            }
        });
    }

    async logSettingsActivity(activityType, settingKey, settingValue) {
        return this.logActivity({
            activityType,
            module: ACTIVITY_MODULES.SETTINGS,
            action: `${activityType}_setting`,
            resourceId: settingKey,
            resourceName: settingKey,
            details: {
                settingKey,
                settingValue
            }
        });
    }

    async logPlacementExamActivity(activityType, examId, examName, details = {}) {
        return this.logActivity({
            activityType,
            module: ACTIVITY_MODULES.PLACEMENT_EXAM,
            action: `${activityType}_exam`,
            resourceId: examId,
            resourceName: examName,
            details
        });
    }

    async getRecentActivities(days = RETENTION_DAYS, limitCount = 100) {
        if (!this.isAdminUser() || !this.firestore) {
            return [];
        }

        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);

            const q = query(
                collection(this.firestore, ACTIVITY_COLLECTION),
                where('userId', '==', this.currentUser.id),
                where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
                orderBy('timestamp', 'desc'),
                limit(limitCount)
            );

            const querySnapshot = await getDocs(q);
            const activities = [];

            querySnapshot.forEach((doc) => {
                activities.push({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate()
                });
            });

            return activities;
        } catch (error) {
            console.error('Failed to fetch admin activities:', error);
            Sentry.captureException(error);
            return [];
        }
    }

    async getActivitiesByModule(module, days = RETENTION_DAYS) {
        if (!this.isAdminUser() || !this.firestore) {
            return [];
        }

        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - days);

            const q = query(
                collection(this.firestore, ACTIVITY_COLLECTION),
                where('userId', '==', this.currentUser.id),
                where('module', '==', module),
                where('timestamp', '>=', Timestamp.fromDate(cutoffDate)),
                orderBy('timestamp', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const activities = [];

            querySnapshot.forEach((doc) => {
                activities.push({
                    id: doc.id,
                    ...doc.data(),
                    timestamp: doc.data().timestamp?.toDate()
                });
            });

            return activities;
        } catch (error) {
            console.error('Failed to fetch activities by module:', error);
            Sentry.captureException(error);
            return [];
        }
    }

    async getLastActivity() {
        if (!this.isAdminUser() || !this.firestore) {
            return null;
        }

        try {
            const q = query(
                collection(this.firestore, ACTIVITY_COLLECTION),
                where('userId', '==', this.currentUser.id),
                orderBy('timestamp', 'desc'),
                limit(1)
            );

            const querySnapshot = await getDocs(q);

            if (querySnapshot.empty) {
                return null;
            }

            const doc = querySnapshot.docs[0];
            return {
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate()
            };
        } catch (error) {
            console.error('Failed to fetch last activity:', error);
            Sentry.captureException(error);
            return null;
        }
    }

    async cleanupOldActivities() {
        if (!this.isAdminUser() || !this.firestore) {
            return 0;
        }

        try {
            const cutoffDate = new Date();
            cutoffDate.setDate(cutoffDate.getDate() - RETENTION_DAYS);

            const q = query(
                collection(this.firestore, ACTIVITY_COLLECTION),
                where('userId', '==', this.currentUser.id),
                where('timestamp', '<', Timestamp.fromDate(cutoffDate))
            );

            const querySnapshot = await getDocs(q);
            let deletedCount = 0;

            const deletePromises = [];
            querySnapshot.forEach((doc) => {
                deletePromises.push(deleteDoc(doc.ref));
                deletedCount++;
            });

            await Promise.all(deletePromises);

            console.log(`Cleaned up ${deletedCount} old activity records`);
            return deletedCount;
        } catch (error) {
            console.error('Failed to cleanup old activities:', error);
            Sentry.captureException(error);
            return 0;
        }
    }

    async getActivityStats(days = RETENTION_DAYS) {
        const activities = await this.getRecentActivities(days, 1000);

        const stats = {
            total: activities.length,
            byType: {},
            byModule: {},
            byDay: {},
            mostActive: {
                module: null,
                type: null
            }
        };

        activities.forEach(activity => {
            stats.byType[activity.activityType] = (stats.byType[activity.activityType] || 0) + 1;
            stats.byModule[activity.module] = (stats.byModule[activity.module] || 0) + 1;

            const day = activity.timestamp?.toISOString().split('T')[0];
            if (day) {
                stats.byDay[day] = (stats.byDay[day] || 0) + 1;
            }
        });

        stats.mostActive.module = Object.keys(stats.byModule).reduce((a, b) => 
            stats.byModule[a] > stats.byModule[b] ? a : b
        , null);

        stats.mostActive.type = Object.keys(stats.byType).reduce((a, b) => 
            stats.byType[a] > stats.byType[b] ? a : b
        , null);

        return stats;
    }
}

export const createAdminActivityLogger = (firestore, currentUser, firebaseUser) => {
    return new AdminActivityLogger(firestore, currentUser, firebaseUser);
};

export default AdminActivityLogger;

